import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
export function TermsOfService() {
  const navigate = useNavigate();
  return <div className="max-w-3xl mx-auto py-12 px-4 animate-in fade-in duration-500" data-id="element-538">
      <button onClick={() => navigate(-1)} className="flex items-center text-sm text-gray-500 hover:text-gray-900 mb-8 transition-colors" data-id="element-539">
        <ArrowLeft className="h-4 w-4 mr-2" data-id="element-540" />
        Back
      </button>

      <h1 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-541">
        Terms of Service
      </h1>
      <p className="text-sm text-gray-500 mb-10" data-id="element-542">Last updated: February 2026</p>

      <div className="prose prose-gray max-w-none space-y-8 text-gray-700 text-[15px] leading-relaxed" data-id="element-543">
        {/* 1. Introduction */}
        <section data-id="element-544">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-545">
            1. Introduction
          </h2>
          <p data-id="element-546">
            These Terms of Service ("Terms") govern your access to and use of
            the MCPGuard website, platform, and services (collectively, the
            "Service") provided by ProvenanceOne ("we", "us", "our"), a company
            registered in England and Wales, trading as MCPGuard.
          </p>
          <p data-id="element-547">
            By accessing or using our Service, you agree to be bound by these
            Terms. If you do not agree to these Terms, you must not use the
            Service. If you are using the Service on behalf of an organisation,
            you represent and warrant that you have the authority to bind that
            organisation to these Terms.
          </p>
        </section>

        {/* 2. Definitions */}
        <section data-id="element-548">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-549">
            2. Definitions
          </h2>
          <ul className="list-disc pl-5 space-y-2 mt-3" data-id="element-550">
            <li data-id="element-551">
              <strong data-id="element-552">"Service"</strong> means the MCPGuard platform, website,
              APIs, documentation, and any related services we provide.
            </li>
            <li data-id="element-553">
              <strong data-id="element-554">"User"</strong> means any individual or entity that
              accesses or uses the Service.
            </li>
            <li data-id="element-555">
              <strong data-id="element-556">"Content"</strong> means any data, text, configurations,
              policies, or other materials submitted to or generated through the
              Service.
            </li>
            <li data-id="element-557">
              <strong data-id="element-558">"Account"</strong> means the account you create to access
              and use the Service.
            </li>
          </ul>
        </section>

        {/* 3. Eligibility */}
        <section data-id="element-559">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-560">
            3. Eligibility
          </h2>
          <p data-id="element-561">
            You must be at least 18 years of age to use the Service. By using
            the Service, you represent and warrant that you meet this
            requirement and that all information you provide is accurate and
            complete.
          </p>
        </section>

        {/* 4. Account Registration */}
        <section data-id="element-562">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-563">
            4. Account Registration
          </h2>
          <p data-id="element-564">
            To access certain features of the Service, you may be required to
            create an Account. You agree to:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3" data-id="element-565">
            <li data-id="element-566">
              Provide accurate, current, and complete information during
              registration.
            </li>
            <li data-id="element-567">Maintain and promptly update your Account information.</li>
            <li data-id="element-568">Keep your login credentials confidential and secure.</li>
            <li data-id="element-569">
              Notify us immediately of any unauthorised use of your Account.
            </li>
          </ul>
          <p className="mt-3" data-id="element-570">
            You are responsible for all activities that occur under your
            Account. We reserve the right to suspend or terminate Accounts that
            violate these Terms.
          </p>
        </section>

        {/* 5. Use of the Service */}
        <section data-id="element-571">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-572">
            5. Use of the Service
          </h2>
          <p data-id="element-573">
            We grant you a limited, non-exclusive, non-transferable, revocable
            licence to access and use the Service in accordance with these
            Terms. You agree not to:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3" data-id="element-574">
            <li data-id="element-575">
              Use the Service for any unlawful purpose or in violation of any
              applicable law or regulation.
            </li>
            <li data-id="element-576">
              Attempt to gain unauthorised access to any part of the Service,
              other Accounts, or any systems or networks connected to the
              Service.
            </li>
            <li data-id="element-577">
              Interfere with or disrupt the integrity or performance of the
              Service.
            </li>
            <li data-id="element-578">
              Reverse engineer, decompile, disassemble, or otherwise attempt to
              derive the source code of the Service.
            </li>
            <li data-id="element-579">
              Use the Service to transmit any malicious code, viruses, or
              harmful content.
            </li>
            <li data-id="element-580">
              Resell, sublicence, or redistribute the Service without our prior
              written consent.
            </li>
            <li data-id="element-581">
              Use the Service in any manner that could damage, disable,
              overburden, or impair our infrastructure.
            </li>
          </ul>
        </section>

        {/* 6. Service Availability */}
        <section data-id="element-582">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-583">
            6. Service Availability
          </h2>
          <p data-id="element-584">
            We strive to maintain the availability of the Service but do not
            guarantee uninterrupted or error-free operation. The Service may be
            subject to scheduled maintenance, updates, or circumstances beyond
            our control. We will endeavour to provide reasonable notice of any
            planned downtime.
          </p>
          <p className="mt-2" data-id="element-585">
            MCPGuard is currently in private preview. Features, functionality,
            and availability may change without notice during this period.
          </p>
        </section>

        {/* 7. Intellectual Property */}
        <section data-id="element-586">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-587">
            7. Intellectual Property
          </h2>
          <p data-id="element-588">
            All intellectual property rights in the Service, including but not
            limited to software, design, text, graphics, logos, and trademarks,
            are owned by or licensed to ProvenanceOne. Nothing in these Terms
            grants you any right, title, or interest in our intellectual
            property except as expressly stated.
          </p>
          <p className="mt-2" data-id="element-589">
            You retain ownership of any Content you submit to the Service. By
            submitting Content, you grant us a limited licence to use, process,
            and store that Content solely for the purpose of providing and
            improving the Service.
          </p>
        </section>

        {/* 8. Data Protection */}
        <section data-id="element-590">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-591">
            8. Data Protection
          </h2>
          <p data-id="element-592">
            We process personal data in accordance with the UK General Data
            Protection Regulation (UK GDPR) and the Data Protection Act 2018.
            Our collection, use, and protection of your personal data is
            described in our{' '}
            <button onClick={() => {
            window.location.hash = 'privacy-policy';
          }} className="text-blue-600 hover:underline font-medium" data-id="element-593">
              Privacy Policy
            </button>
            , which forms part of these Terms.
          </p>
          <p className="mt-2" data-id="element-594">
            Where you use the Service to process personal data on behalf of your
            organisation, you are responsible for ensuring that such processing
            complies with applicable data protection laws. We will act as a data
            processor in respect of such data and will process it only in
            accordance with your instructions and our Data Processing Agreement
            (where applicable).
          </p>
        </section>

        {/* 9. Confidentiality */}
        <section data-id="element-595">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-596">
            9. Confidentiality
          </h2>
          <p data-id="element-597">
            Each party agrees to keep confidential any non-public information
            disclosed by the other party in connection with the Service
            ("Confidential Information"). Confidential Information shall not be
            disclosed to any third party without the prior written consent of
            the disclosing party, except as required by law or regulation.
          </p>
        </section>

        {/* 10. Fees and Payment */}
        <section data-id="element-598">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-599">
            10. Fees and Payment
          </h2>
          <p data-id="element-600">
            Access to the Service during the private preview period is provided
            free of charge. We reserve the right to introduce fees for the
            Service or specific features in the future. If we do, we will
            provide reasonable notice and you will have the option to accept the
            new terms or discontinue use of the Service.
          </p>
          <p className="mt-2" data-id="element-601">
            All fees, once introduced, will be exclusive of VAT and any other
            applicable taxes unless stated otherwise.
          </p>
        </section>

        {/* 11. Limitation of Liability */}
        <section data-id="element-602">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-603">
            11. Limitation of Liability
          </h2>
          <p data-id="element-604">To the maximum extent permitted by law:</p>
          <ul className="list-disc pl-5 space-y-2 mt-3" data-id="element-605">
            <li data-id="element-606">
              The Service is provided on an "as is" and "as available" basis
              without warranties of any kind, whether express or implied,
              including but not limited to implied warranties of
              merchantability, fitness for a particular purpose, and
              non-infringement.
            </li>
            <li data-id="element-607">
              We shall not be liable for any indirect, incidental, special,
              consequential, or punitive damages, including but not limited to
              loss of profits, data, business, or goodwill, arising out of or in
              connection with your use of the Service.
            </li>
            <li data-id="element-608">
              Our total aggregate liability to you for any claims arising under
              or in connection with these Terms shall not exceed the greater of
              (a) the total fees paid by you to us in the twelve (12) months
              preceding the claim, or (b) £100.
            </li>
          </ul>
          <p className="mt-3" data-id="element-609">
            Nothing in these Terms excludes or limits our liability for: (a)
            death or personal injury caused by our negligence; (b) fraud or
            fraudulent misrepresentation; or (c) any other liability that cannot
            be excluded or limited under the laws of England and Wales.
          </p>
        </section>

        {/* 12. Indemnification */}
        <section data-id="element-610">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-611">
            12. Indemnification
          </h2>
          <p data-id="element-612">
            You agree to indemnify, defend, and hold harmless ProvenanceOne and
            its officers, directors, employees, and agents from and against any
            claims, liabilities, damages, losses, and expenses (including
            reasonable legal fees) arising out of or in connection with: (a)
            your use of the Service; (b) your violation of these Terms; or (c)
            your violation of any rights of a third party.
          </p>
        </section>

        {/* 13. Termination */}
        <section data-id="element-613">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-614">
            13. Termination
          </h2>
          <p data-id="element-615">
            We may suspend or terminate your access to the Service at any time,
            with or without cause, and with or without notice. You may stop
            using the Service at any time.
          </p>
          <p className="mt-2" data-id="element-616">
            Upon termination: (a) your right to access and use the Service will
            immediately cease; (b) we may delete your Account and associated
            Content after a reasonable period; and (c) provisions of these Terms
            that by their nature should survive termination will continue to
            apply, including Sections 7, 9, 11, 12, and 15.
          </p>
        </section>

        {/* 14. Changes to These Terms */}
        <section data-id="element-617">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-618">
            14. Changes to These Terms
          </h2>
          <p data-id="element-619">
            We reserve the right to modify these Terms at any time. If we make
            material changes, we will notify you by updating the date at the top
            of these Terms and, where appropriate, providing additional notice
            (such as by email). Your continued use of the Service after any
            changes constitutes your acceptance of the updated Terms.
          </p>
        </section>

        {/* 15. Governing Law and Jurisdiction */}
        <section data-id="element-620">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-621">
            15. Governing Law and Jurisdiction
          </h2>
          <p data-id="element-622">
            These Terms are governed by and construed in accordance with the
            laws of England and Wales. Any disputes arising out of or in
            connection with these Terms shall be subject to the exclusive
            jurisdiction of the courts of England and Wales.
          </p>
        </section>

        {/* 16. General */}
        <section data-id="element-623">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-624">
            16. General Provisions
          </h2>
          <ul className="list-disc pl-5 space-y-2 mt-3" data-id="element-625">
            <li data-id="element-626">
              <strong data-id="element-627">Entire Agreement:</strong> These Terms, together with our
              Privacy Policy, constitute the entire agreement between you and
              ProvenanceOne regarding the Service.
            </li>
            <li data-id="element-628">
              <strong data-id="element-629">Severability:</strong> If any provision of these Terms is
              found to be unenforceable, the remaining provisions will continue
              in full force and effect.
            </li>
            <li data-id="element-630">
              <strong data-id="element-631">Waiver:</strong> Our failure to enforce any right or
              provision of these Terms shall not constitute a waiver of that
              right or provision.
            </li>
            <li data-id="element-632">
              <strong data-id="element-633">Assignment:</strong> You may not assign or transfer your
              rights under these Terms without our prior written consent. We may
              assign our rights and obligations without restriction.
            </li>
            <li data-id="element-634">
              <strong data-id="element-635">Force Majeure:</strong> We shall not be liable for any
              failure or delay in performing our obligations where such failure
              or delay results from circumstances beyond our reasonable control.
            </li>
          </ul>
        </section>

        {/* 17. Contact */}
        <section data-id="element-636">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-637">
            17. Contact Us
          </h2>
          <p data-id="element-638">If you have any questions about these Terms, please contact us:</p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm space-y-1 mt-2" data-id="element-639">
            <p className="font-medium text-gray-900" data-id="element-640">ProvenanceOne</p>
            <p data-id="element-641">Email: privacy@provenanceone.ai</p>
          </div>
        </section>
      </div>
    </div>;
}